import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Download, X, Smartphone } from 'lucide-react';
import { usePWA } from '@/hooks/usePWA';
import { useIsMobile } from '@/hooks/use-mobile';
import { productionLogger } from '@/utils/production-logger';

export const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [hasBeenDismissed, setHasBeenDismissed] = useState(false);
  const { isInstallable, isInstalled, promptInstall, isIOS } = usePWA();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
    
    // Check if user has already dismissed the prompt
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      setHasBeenDismissed(true);
    }

    // Show prompt after 30 seconds if installable and not dismissed
    const timer = setTimeout(() => {
      if (isInstallable && !hasBeenDismissed && !isInstalled) {
        setShowPrompt(true);
      }
    }, 30000); // Back to 30 seconds for production

    return () => clearTimeout(timer);
  }, [isInstallable, hasBeenDismissed, isInstalled]);

  const handleInstall = async () => {
    const success = await promptInstall();
    if (success) {
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setHasBeenDismissed(true);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('pwa-prompt-dismissed', 'true');
    }
  };

  // Don't show if not installable, already installed, or dismissed
  if (!isInstallable || isInstalled || hasBeenDismissed || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-sm">
      <Card className="border-primary/20 bg-gradient-to-br from-brand-cream to-background shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-primary" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm mb-1">
                Add to Home Screen
              </h3>
              {isIOS ? (
                <>
                  <p className="text-xs text-muted-foreground mb-3">
                    Tap the Share button <span className="font-semibold">⎋</span> below, then "Add to Home Screen"
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={handleDismiss}
                      className="h-8 text-xs flex-1"
                    >
                      Got it
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mb-3">
                    Get quick access to Boho Style Co. right from your {isMobile ? 'phone' : 'desktop'}
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={handleInstall}
                      className="h-8 text-xs bg-primary hover:bg-primary-glow flex-1"
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Install
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={handleDismiss}
                      className="h-8 w-8 p-0"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};